/* ORCHID — the nav panels.
 *
 * Hover on a pointer, focus on a keyboard, and nothing at all on a touch screen
 * or below 1024px, where the header's own Menu panel is the navigation.
 *
 * The three things that make a hover menu feel right, and that most of them get
 * wrong:
 *
 *   1. Intent. Opening on the first pixel of hover means the menu flashes at
 *      anyone whose cursor crosses the bar on its way somewhere else. It opens
 *      after a short pause.
 *   2. The diagonal. Leaving a trigger to reach its panel means travelling
 *      diagonally, and for part of that journey the cursor is over neither. A
 *      close on the first mouseleave kills the menu underneath the hand that is
 *      reaching for it. Closing is therefore delayed, and any re-entry within
 *      that window cancels it.
 *   3. Escape and focus. A menu you cannot leave with the keyboard is a trap:
 *      Escape closes and returns focus to its trigger, Tab out closes behind you.
 *
 * Written to the same rules as the rest: no third-party anything, no images in
 * the script, transform and opacity only, idempotent for the theme editor.
 */
(function () {
  'use strict';
  if (window.orchidMenuLoaded) return;
  window.orchidMenuLoaded = true;

  var OPEN_DELAY = 90;    // long enough to ignore a cursor passing through
  var CLOSE_DELAY = 220;  // long enough to cross the gap to the panel
  var bound = new WeakSet();

  var openTimer = null;
  var closeTimer = null;
  var current = null;     // { trigger, panel }

  function canHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
           window.matchMedia('(min-width: 1024px)').matches;
  }

  function show(pair) {
    window.clearTimeout(closeTimer);
    if (current && current.panel !== pair.panel) hide(current, true);
    current = pair;
    pair.panel.classList.add('is-open');
    pair.trigger.setAttribute('aria-expanded', 'true');
  }

  function hide(pair, immediate) {
    if (!pair) return;
    pair.panel.classList.remove('is-open');
    pair.trigger.setAttribute('aria-expanded', 'false');
    if (current === pair) current = null;
    if (immediate) window.clearTimeout(closeTimer);
  }

  function scheduleOpen(pair) {
    window.clearTimeout(closeTimer);
    window.clearTimeout(openTimer);
    // Already showing something: swap at once. Hesitating between two open
    // menus reads as lag, not as care.
    if (current) { show(pair); return; }
    openTimer = window.setTimeout(function () { show(pair); }, OPEN_DELAY);
  }

  function scheduleClose(pair) {
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(function () { hide(pair); }, CLOSE_DELAY);
  }

  function wire(trigger) {
    if (bound.has(trigger)) return;
    var id = trigger.getAttribute('aria-controls');
    var panel = id && document.getElementById(id);
    if (!panel) return;
    bound.add(trigger);

    var pair = { trigger: trigger, panel: panel };
    var item = trigger.closest('.orchid-nav-item') || trigger;

    [item, panel].forEach(function (zone) {
      zone.addEventListener('mouseenter', function () {
        if (!canHover()) return;
        scheduleOpen(pair);
      });
      zone.addEventListener('mouseleave', function () {
        if (!canHover()) return;
        scheduleClose(pair);
      });
    });

    // Keyboard. The trigger is a real link, so Enter still follows it to the
    // section page; focus alone opens the panel so Tab walks straight into it.
    trigger.addEventListener('focus', function () { show(pair); });

    [item, panel].forEach(function (zone) {
      zone.addEventListener('focusout', function (event) {
        // Focus moving within the same item or its panel is not leaving.
        var to = event.relatedTarget;
        if (to && (item.contains(to) || panel.contains(to))) return;
        hide(pair);
      });
    });

    panel.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      hide(pair);
      trigger.focus();
    });
    trigger.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      hide(pair);
    });
  }

  function init() {
    var triggers = document.querySelectorAll('[data-orchid-menu-trigger]');
    Array.prototype.forEach.call(triggers, wire);
  }

  // A pointer leaving the whole header closes whatever is open, so a menu can
  // never be left hanging over the page after the cursor has gone elsewhere.
  document.addEventListener('mouseover', function (event) {
    if (!current) return;
    var header = document.querySelector('.orchid-header');
    if (header && !header.contains(event.target)) {
      window.clearTimeout(openTimer);
      hide(current);
    }
  }, { passive: true });

  // Scrolling compacts the header and takes the nav with it; anything open
  // should go too rather than float free of the bar it belongs to.
  window.addEventListener('scroll', function () {
    if (current) hide(current);
  }, { passive: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);

  /* --- Straight to a tier from the menu ---------------------------------
   * A menu link can point at /?tier=card_classic, which lands on the homepage
   * and opens that tier's chooser: from hovering the nav to choosing The
   * Classic is two clicks and no reading. Same mechanism the Twin-Gift uses,
   * kept here because it is the menu that generates these links.
   */
  function openTier() {
    var m = window.location.search.match(/[?&]tier=([^&#]*)/);
    if (!m) return;
    var tier = decodeURIComponent(m[1]).split('#')[0];
    if (!/^[A-Za-z0-9_-]{1,40}$/.test(tier)) return;
    var trigger = document.querySelector('[data-orchid-chooser][data-tier="' + tier + '"]');
    if (!trigger) return;
    window.setTimeout(function () { trigger.click(); }, 80);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openTier);
  else openTier();
})();
